import { useEffect, useState, useMemo, useRef, type ChangeEvent } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import RightTimeApi from "@/API";
import Modal from "../modalSrc/Modal";
import { toast } from "react-toastify";
import RequestFormFieldsTable from "../tables/RequestFormFieldsTable";
import { isDepartmentRequestField } from "@/utils/formSelectionOptions";

type TestTypeField = {
  id: number;
  sequence: number;
  required: boolean;
  proctorProvided: boolean;
  label: string;
  testRequestFieldId: number;
  testRequestFieldName?: string;
};

type TestTypeItem = {
  id: number;
  name: string;
  active?: boolean;
  fields?: TestTypeField[];
};

type FieldRow = {
  id: number;
  fieldId: number;
  name: string;
  required: boolean;
  proctorProvided: boolean;
  testRequestFieldName?: string;
};

type CopySourceItem = {
  id: number;
  companyId: number;
  companyName: string;
  name: string;
  testTypeName: string;
};

type PickerMode = "create" | "copy";

export type CreatePickerResult = {
  mode: "create";
  id: number;
  name: string;
  fields: Array<{ fieldId: number; name: string; required: boolean; proctorProvided: boolean }>;
};

export type CopyPickerResult = {
  mode: "copy";
  sourceCttIds: number[];
};

export type PickerResult = CreatePickerResult | CopyPickerResult;

type TabData = {
  company?: {
    id?: number;
    departmentsEnabled?: boolean;
  };
};

type TestTypePickerProps = {
  isOpen: boolean;
  onClose: (result?: PickerResult) => void;
  tabData?: TabData;
};

function toggleId(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

export default function TestTypePicker({ isOpen, onClose, tabData }: TestTypePickerProps) {
  const [mode, setMode] = useState<PickerMode>("create");
  const [selectedTestTypeId, setSelectedTestTypeId] = useState("");
  const [name, setName] = useState("");
  const [testTypes, setTestTypes] = useState<TestTypeItem[]>([]);
  const [seedFields, setSeedFields] = useState<FieldRow[]>([]);
  const [copySources, setCopySources] = useState<CopySourceItem[]>([]);
  const [copySourcesLoading, setCopySourcesLoading] = useState(false);
  const [selectedCopySourceIds, setSelectedCopySourceIds] = useState<number[]>([]);
  const currentFieldsRef = useRef<FieldRow[]>([]);

  const targetCompanyId = tabData?.company?.id ?? 0;
  const departmentsEnabled = tabData?.company?.departmentsEnabled !== false;

  const stableTestTypeFields = useMemo(() =>
    seedFields.map((r) => ({
      id: r.fieldId,
      testRequestFieldId: r.fieldId,
      label: r.name,
      required: r.required,
      proctorProvided: r.proctorProvided,
      testRequestFieldName: r.testRequestFieldName,
    })), [seedFields]);

  const stableFieldTypes = useMemo(() =>
    seedFields.map((r) => ({ id: r.fieldId, name: r.testRequestFieldName || r.name })),
    [seedFields]);

  function filterSeedFieldsForCompany(rows: FieldRow[]): FieldRow[] {
    if (departmentsEnabled) return rows;
    return rows.filter((row) => !isDepartmentRequestField({
      fieldId: row.fieldId,
      fieldElementName: row.testRequestFieldName,
    }));
  }

  useEffect(() => {
    if (!isOpen) {
      setMode("create");
      setSelectedTestTypeId("");
      setName("");
      setSeedFields([]);
      setCopySources([]);
      setSelectedCopySourceIds([]);
      return;
    }

    let cancelled = false;

    async function fetchCreateData() {
      const resp = await RightTimeApi.getAsync("/TestTypes");
      if (cancelled) return;
      if (resp?.ok && resp.jsonData?.testTypes && Array.isArray(resp.jsonData.testTypes)) {
        const list = (resp.jsonData.testTypes as TestTypeItem[])
          .filter((tt) => tt.active)
          .sort((a, b) => a.name.localeCompare(b.name));
        setTestTypes(list);
      } else {
        setTestTypes([]);
      }
    }

    fetchCreateData();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== "copy" || !targetCompanyId) {
      return;
    }

    let cancelled = false;
    setCopySourcesLoading(true);

    async function fetchCopySources() {
      const resp = await RightTimeApi.getAsync(
        `/CompanyTestTypes/copySources?targetCompanyId=${targetCompanyId}`
      );
      if (cancelled) return;
      if (resp?.ok && Array.isArray(resp.jsonData?.companyTestTypes)) {
        setCopySources(resp.jsonData.companyTestTypes as CopySourceItem[]);
      } else {
        setCopySources([]);
      }
      setCopySourcesLoading(false);
    }

    fetchCopySources();
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, targetCompanyId]);

  function handleSelectChange(e: ChangeEvent<HTMLSelectElement>) {
    const nextId = e.target.value;
    setSelectedTestTypeId(nextId);

    const selected = testTypes.find((tt) => String(tt.id) === nextId);
    setName(selected?.name || "");

    const rows: FieldRow[] = (selected?.fields ?? [])
      .sort((a, b) => a.sequence - b.sequence)
      .map((f) => {
        const requestFieldId = f.testRequestFieldId ?? f.id;
        return {
          id: requestFieldId,
          fieldId: requestFieldId,
          name: f.label,
          required: f.required,
          proctorProvided: f.proctorProvided,
          testRequestFieldName: f.testRequestFieldName,
        };
      });
    const filtered = filterSeedFieldsForCompany(rows);
    setSeedFields(filtered);
    currentFieldsRef.current = filtered;
  }

  function handleTestTypeFieldsChange(nextRows: Array<{
    id: number;
    testRequestFieldId: number;
    testRequestFieldName?: string;
    label: string;
    required: boolean;
    proctorProvided: boolean;
  }>) {
    currentFieldsRef.current = nextRows.map((row) => ({
      id: row.id,
      fieldId: row.testRequestFieldId,
      name: row.label,
      required: !!row.required,
      proctorProvided: !!row.proctorProvided,
      testRequestFieldName: row.testRequestFieldName,
    }));
  }

  function handleModeChange(nextMode: PickerMode) {
    setMode(nextMode);
    setSelectedCopySourceIds([]);
  }

  function toggleCopySourceSelection(sourceId: number) {
    setSelectedCopySourceIds((prev) => toggleId(prev, sourceId));
  }

  async function handleAdd() {
    if (mode === "copy") {
      if (selectedCopySourceIds.length === 0) {
        toast.error("Please select at least one company test type to copy.");
        return;
      }
      onClose({ mode: "copy", sourceCttIds: selectedCopySourceIds });
      return;
    }

    if (!selectedTestTypeId) {
      toast.error("Please select a test type.");
      return;
    }
    if (!name.trim()) {
      toast.error("Please enter a name.");
      return;
    }
    onClose({
      mode: "create",
      id: parseInt(selectedTestTypeId, 10),
      name: name.trim(),
      fields: currentFieldsRef.current.map((r) => ({
        fieldId: r.fieldId,
        name: r.name,
        required: r.required,
        proctorProvided: r.proctorProvided,
      })),
    });
  }

  function handleCancel() {
    onClose();
  }

  const footer = (
    <div className="footerRight">
      <button
        className="btn btn-primary okButton"
        onClick={handleAdd}
        disabled={
          mode === "create"
            ? !selectedTestTypeId || !name.trim()
            : selectedCopySourceIds.length === 0
        }
      >
        {mode === "copy"
          ? selectedCopySourceIds.length > 1
            ? `Copy (${selectedCopySourceIds.length})`
            : "Copy"
          : "Add Test Type"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Add Test Type"
      className="addTestTypeModal"
      size="lg"
      footer={footer}
    >
      <div className="addTestTypeModeToggle">
        <button
          type="button"
          className={`addTestTypeModeButton${mode === "create" ? " active" : ""}`}
          onClick={() => handleModeChange("create")}
        >
          Create
        </button>
        <button
          type="button"
          className={`addTestTypeModeButton${mode === "copy" ? " active" : ""}`}
          onClick={() => handleModeChange("copy")}
        >
          Copy
        </button>
      </div>

      {mode === "create" ? (
        <>
          <div className="addTestTypeFieldRow">
            <label htmlFor="testTypeSelect" className="addTestTypeLabel">Test Type:</label>
            <select
              id="testTypeSelect"
              className="testTypeSelect addTestTypeSelect"
              value={selectedTestTypeId}
              onChange={handleSelectChange}
            >
              <option value="">-- Select --</option>
              {testTypes.map((tt) => (
                <option key={tt.id} value={tt.id}>
                  {tt.name}
                </option>
              ))}
            </select>
          </div>
          <div className="addTestTypeFieldRow">
            <label htmlFor="nameInput" className="addTestTypeLabel">Name:</label>
            <input
              id="nameInput"
              className="nameInput addTestTypeNameInput"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name"
            />
          </div>
          {seedFields.length > 0 && (
            <RequestFormFieldsTable
              mode="testTypeDraft"
              fieldTypes={stableFieldTypes}
              testTypeFields={stableTestTypeFields}
              onTestTypeFieldsChange={handleTestTypeFieldsChange}
              departmentsEnabled={departmentsEnabled}
            />
          )}
        </>
      ) : (
        <div className="addTestTypeCopyList">
          {copySourcesLoading ? (
            <p className="addTestTypeCopyStatus">Loading company test types…</p>
          ) : copySources.length === 0 ? (
            <p className="addTestTypeCopyStatus">No active company test types available to copy.</p>
          ) : (
            <table className="addTestTypeCopyTable wizardCheckboxTable">
              <thead>
                <tr>
                  <th className="actions">Select</th>
                  <th>Company</th>
                  <th>CTT Name</th>
                  <th>Test Type</th>
                </tr>
              </thead>
              <tbody>
                {copySources.map((source) => {
                  const isSelected = selectedCopySourceIds.includes(source.id);
                  return (
                  <tr
                    key={source.id}
                    className={isSelected ? "selected" : ""}
                    onClick={() => toggleCopySourceSelection(source.id)}
                  >
                    <td className="actions">
                      {isSelected ? (
                        <MdCheckBox className="checkbox checked actionIcon" />
                      ) : (
                        <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                      )}
                    </td>
                    <td className="left">{source.companyName}</td>
                    <td className="left">{source.name}</td>
                    <td className="left">{source.testTypeName}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Modal>
  );
}
